import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { GoldButton } from "@/components/GoldButton";
import { useWaitlist } from "@/context/WaitlistContext";
import { toast } from "sonner";
import { Coins, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const WaitlistModal = () => {
  const { open, setOpen } = useWaitlist();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setDone(true);
    toast.success("You're on the list — welcome to Shreedhan.");
    setTimeout(() => {
      setOpen(false);
      setTimeout(() => {
        setDone(false);
        setEmail("");
        setName("");
      }, 300);
    }, 1600);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="waitlist-modal"
        className="max-w-md overflow-hidden rounded-3xl border-amber-200/60 bg-ivory-light p-0"
      >
        <div className="relative p-8">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gold/20 blur-3xl" />
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center py-6 text-center"
              >
                <CheckCircle2 className="mb-4 h-14 w-14 text-gold" />
                <h3 className="font-serif text-2xl text-charcoal">You're in.</h3>
                <p className="mt-2 text-sm text-charcoal-soft">
                  We'll email you when your golden vault is ready.
                </p>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-gold">
                  <Coins className="h-3.5 w-3.5" /> Early access
                </div>
                <DialogHeader className="text-left">
                  <DialogTitle className="font-serif text-3xl font-normal leading-tight text-charcoal">
                    Start your golden habit
                  </DialogTitle>
                  <DialogDescription className="text-charcoal-soft">
                    Join the waitlist for Shreedhan — the gold wealth platform for the Indian world.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="mt-6 space-y-3">
                  <Input
                    data-testid="waitlist-name-input"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 rounded-xl border-amber-200/60 bg-white/70 focus-visible:ring-gold"
                  />
                  <Input
                    data-testid="waitlist-email-input"
                    type="email"
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl border-amber-200/60 bg-white/70 focus-visible:ring-gold"
                  />
                  <GoldButton data-testid="waitlist-submit-btn" type="submit" size="lg" className="w-full">
                    Claim my spot
                  </GoldButton>
                </form>
                <p className="mt-4 text-center text-[11px] leading-relaxed text-charcoal-soft/70">
                  Digital gold is not a bank deposit or an insured product. Capital is at risk.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
